function createRule(md, ruleName, styleProperty, regex, styleExtractor) {
  function tokenize(state, silent) {
    // Match opening tag
    const openMatch = state.src.slice(state.pos).match(regex.open);
    if (openMatch) {
      if (!silent) {
        const token = state.push('text', '', 0);
        token.content = openMatch[0];

        state.delimiters.push({
          marker: '[', // Marker for the rule
          length: 0,
          token: state.tokens.length - 1,
          end: -1, // Will be updated in postProcess
          open: true,
          close: false,
          styleValue: styleExtractor(openMatch), // Extract the style value
        });
      }
      state.pos += openMatch[0].length;
      return true;
    }

    // Match closing tag
    const closeMatch = state.src.slice(state.pos).match(regex.close);
    if (closeMatch) {
      if (!silent) {
        const token = state.push('text', '', 0);
        token.content = closeMatch[0];

        state.delimiters.push({
          marker: '[', // Marker for the rule
          length: 0,
          token: state.tokens.length - 1,
          end: -1, // Will be updated in postProcess
          open: false,
          close: true,
        });
      }
      state.pos += closeMatch[0].length;
      return true;
    }

    return false;
  }

  function postProcess(state, delimiters) {
    const max = delimiters.length;
    const stack = []; // Stack to track unmatched opening delimiters

    for (let i = 0; i < max; i++) {
      const delim = delimiters[i];

      if (delim.marker !== '[') {
        continue; // Skip if it's not a matching marker
      }

      if (delim.open) {
        stack.push(i); // Push the index of the opening delimiter onto the stack
      } else if (delim.close) {
        if (stack.length === 0) {
          continue; // No matching opening delimiter
        }

        const openIndex = stack.pop(); // Get the matching opening delimiter
        const openDelim = delimiters[openIndex];

        // Update the `end` properties of the matching delimiters
        openDelim.end = i;
        delim.end = openIndex;

        // Replace the opening token
        const tokenOpen = state.tokens[openDelim.token];
        tokenOpen.type = `${ruleName}_open`;
        tokenOpen.tag = 'span';
        tokenOpen.nesting = 1;

        // Ensure styles are correctly mapped to their properties
        const existingStyle = tokenOpen.attrs?.find(attr => attr[0] === 'style');
        const newStyle = `${styleProperty}: ${openDelim.styleValue}`;
        if (existingStyle) {
          const stylesMap = Object.fromEntries(
            existingStyle[1].split(';').map(style => style.trim().split(':').map(s => s.trim()))
          );
          stylesMap[styleProperty] = openDelim.styleValue; // Update or add the new style
          existingStyle[1] = Object.entries(stylesMap).map(([key, value]) => `${key}: ${value}`).join('; ');
        } else {
          tokenOpen.attrs = [['style', newStyle]];
        }

        tokenOpen.markup = `[${ruleName}=...]`;
        tokenOpen.content = '';

        // Replace the closing token
        const tokenClose = state.tokens[delim.token];
        tokenClose.type = `${ruleName}_close`;
        tokenClose.tag = 'span';
        tokenClose.nesting = -1;
        tokenClose.markup = `[/${ruleName}]`;
        tokenClose.content = '';
      }
    }
  }

  md.inline.ruler.before('emphasis', ruleName, tokenize);
  md.inline.ruler2.before('emphasis', ruleName, function (state) {
    const tokens_meta = state.tokens_meta;
    const max = state.tokens_meta.length;

    postProcess(state, state.delimiters);

    for (let curr = 0; curr < max; curr++) {
      if (tokens_meta[curr] && tokens_meta[curr].delimiters) {
        postProcess(state, tokens_meta[curr].delimiters);
      }
    }
  });

  md.renderer.rules[`${ruleName}_open`] = (tokens, idx) => {
    const style = tokens[idx].attrs.find(attr => attr[0] === 'style')[1];
    return `<span style="${style}">`;
  };

  md.renderer.rules[`${ruleName}_close`] = () => {
    return '</span>';
  };
}

export function createFontSizeRule(md) {
  createRule(
    md,
    'fontSize',
    'font-size',
    {
      open: /^\[fontSize=(\d+)\]/,
      close: /^\[\/fontSize\]/,
    },
    match => `${match[1]}px` // Extract font size and append "px"
  );
}

export function createTextColorRule(md) {
  createRule(
    md,
    'textColor',
    'color',
    {
      open: /^\[textColor=([#a-zA-Z0-9]+)\]/,
      close: /^\[\/textColor\]/,
    },
    match => match[1] // Extract the color value directly
  );
}

export function createFontFamilyRule(md) {
  createRule(
    md,
    'fontFamily',
    'font-family',
    {
      open: /^\[fontFamily=([a-zA-Z\s\-]+)\]/,
      close: /^\[\/fontFamily\]/,
    },
    match => match[1] // Extract font family directly
  );
}

function createAlignmentRule(md, ruleName) {
  const alignment = ruleName.replace('align', '').toLowerCase();

  md.block.ruler.before(
    'paragraph',
    ruleName,
    (state, startLine, endLine, silent) => {
      const pos = state.bMarks[startLine] + state.tShift[startLine];
      const max = state.eMarks[startLine];
      const lineText = state.src.slice(pos, max);

      const openTag = `[${ruleName}]`;
      if (!lineText.startsWith(openTag)) return false;

      let contentLines = [];
      let currentLine = startLine;
      let foundClose = false;

      const closeTag = `[/${ruleName}]`;
      if (lineText.includes(closeTag)) {
        const content = lineText
          .replace(openTag, '')
          .replace(closeTag, '')
          .trim();
        contentLines.push(content);
        foundClose = true;
        currentLine = startLine + 1;
      } else {
        const firstContent = lineText.replace(openTag, '').trim();
        if (firstContent) contentLines.push(firstContent);

        for (
          currentLine = startLine + 1;
          currentLine < endLine;
          currentLine += 1
        ) {
          const nextPos = state.bMarks[currentLine] + state.tShift[currentLine];
          const nextMax = state.eMarks[currentLine];
          const nextLine = state.src.slice(nextPos, nextMax);

          if (nextLine.includes(closeTag)) {
            const lastContent = nextLine.replace(closeTag, '').trim();
            if (lastContent) contentLines.push(lastContent);
            foundClose = true;
            currentLine += 1;
            break;
          }
          contentLines.push(nextLine);
        }
      }

      if (!foundClose) return false;
      if (silent) return true;

      const token = state.push('paragraph_open', 'p', 1);
      token.attrs = [['style', `text-align: ${alignment}`]];
      token.map = [startLine, currentLine];

      const contentToken = state.push('inline', '', 0);
      contentToken.content = contentLines.join('\n');
      contentToken.map = [startLine, currentLine];
      contentToken.children = [];

      state.push('paragraph_close', 'p', -1);

      state.line = currentLine;
      return true;
    }
  );
}

export default md => {
  createFontSizeRule(md);
  createFontFamilyRule(md);
  createTextColorRule(md);

  ['alignLeft', 'alignRight', 'alignCenter', 'alignJustify'].forEach(
    ruleName => {
      createAlignmentRule(md, ruleName);
    }
  );
};
